import { Form } from "antd";
import {
  FormFieldName,
  FormHeaderTextarea,
  FormSelect,
  FormSelectWithSearch,
} from "../../../../components/form/form";
import TagsSelect from "../../../../components/tags-select/tags-select";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Drawer } from "../../../../components/drawer/drawer";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/slices/user";
import ApiFact from "../../../../api/life-facts";
import SimpleColorPicker from "../../../../components/color-picker/color-picker";
import "../life-facts.css";
import { useTranslation } from "react-i18next";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import debounce from "../../../../common/utils/debounce";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import DiscardModal from "../../../../components/modals/discard/discard-modal.js";
import ChangesDiffModalComponent from "../../../../components/modals/change-logs/changes-diff-modal.js";
import AssociationsBlock from "../../../../components/associations/associations-block";

const LifeFactsModal = ({
  form,
  data,
  onClose,
  onSave,
  authors,
  categories,
  articleId,
  factCitations,
  factUrls,
  tags,
  isLoading,
  mode,
  onDiscard,
  onPublish,
  onSaveSilent,
  remove,
  toggleActive,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);

  // ---- NEW: full fact loaded by id ----
  const [fetchedFact, setFetchedFact] = useState(null);
  // const [isLoadingFact, setIsLoadingFact] = useState(false);

  const currentUser = useSelector(selectUser);
  const navigate = useProjectNavigate();
  const urlPart = "/content/fitfacts";

  const [isChanged, setChanged] = useState(false);

  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);
  const openDiscardModal = () => setDiscardModalOpen(true);
  const closeDiscardModal = () => setDiscardModalOpen(false);

  // --- Diff modal state ---
  const [isOpenChangeLogsModal, setOpenChangeLogsModal] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);

  const diffFields = useMemo(
    () => [
      { key: "text", label: "Text", type: "text" },
      {
        key: "categoryId",
        label: "Category",
        type: "entity",
        resolve: (id) => categories.find((c) => c.id === id)?.name || "—",
        getId: (id) => id,
      },
      {
        key: "tags",
        label: "Tags",
        type: "array-entity",
        resolveMany: (vals) =>
          (vals || []).map((v) => {
            if (typeof v === "object") return v.name ?? v.title ?? v.id;
            const tag = tags.find((t) => t.id === v);
            return tag?.name ?? String(v);
          }),
        getId: (v) => (typeof v === "object" ? v.id : v),
      },
      {
        key: "userId",
        label: "Author",
        type: "entity",
        resolve: (id) => authors.find((a) => a.id === id)?.fullName || "—",
        getId: (id) => id,
      },
      {
        key: "FactUrl",
        label: "Source URL",
        type: "entity",
        resolve: (obj) => obj?.source || "—",
        getId: (obj) => obj?.id,
      },
      {
        key: "FactCitation",
        label: "Source citation",
        type: "entity",
        resolve: (obj) => obj?.source || "—",
        getId: (obj) => obj?.id,
      },
      { key: "color", label: "Color", type: "color" },
    ],
    [authors, categories, tags]
  );

  // ---- NEW: choose data source for rendering + merge associations from fetchedFact ----
  const viewData = useMemo(() => {
    const base = data;

    if (base && !base.associations && fetchedFact?.associations) {
      return { ...base, associations: fetchedFact.associations };
    }

    return base;
  }, [data, fetchedFact]);

  useEffect(() => {
    if (data?.status === "pending") {
      setChanged(true);
    }
  }, [data?.status]);

  const debouncedAutoSave = useCallback(
    debounce((fields) => {
      if (data?.id) {
        onSaveSilent?.(data.id, fields);
      }
    }, 500),
    [data?.id, onSaveSilent]
  );

  const handleFieldChange = (field, value) => {
    form.setFieldValue(field, value);
    const fields = form.getFieldsValue();
    debouncedAutoSave({ ...fields, [field]: value });
    setChanged(true);
  };

  useEffect(() => {
    return () => debouncedAutoSave.cancel?.();
  }, [debouncedAutoSave]);

  useEffect(() => {
    if (mode === "edit") setEditMode(true);
    if (mode === "create") setEditMode(true);
    if (mode === "show") setEditMode(false);
  }, [mode]);

  const closeHandler = () => {
    navigate(urlPart);
    setEditMode(false);
    onClose();
  };

  const editHandler = () => {
    navigate(`${urlPart}/${articleId}/edit`);
    setEditMode(true);
  };

  const handleDiscard = async () => {
    try {
      setChanged(false);
      onClose();
      await onDiscard?.(data.id);
    } catch (error) {
      console.error("Discard error:", error);
    }
  };

  const handlePublish = async () => {
    try {
      await onPublish?.(data.id);
      setChanged(false);
      navigate(`/content/fitfacts/${data.id}`);
      setEditMode(false);
    } catch (error) {
      console.error("Publish error:", error);
    }
  };

  const cancelHandler = () => {
    if (articleId) {
      navigate(`${urlPart}/${articleId}`);
    } else {
      navigate(`${urlPart}`);
    }
    setEditMode(false);
    if (data.status === "draft") {
      onClose();
    }
  };

  const updateSource = (value) => handleFieldChange("FactUrl", value);
  const updateCitation = (value) => handleFieldChange("FactCitation", value);
  const updateColor = (color) =>
    handleFieldChange("color", color?.backgroundColor);

  const updateAuthor = (author) => {
    const authorId = authors.find((a) => a.fullName === author)?.id;
    handleFieldChange("userId", authorId);
  };

  const updateTags = (values) => handleFieldChange("tags", values);
  const updateCategory = (value) => handleFieldChange("categoryId", value);

  useEffect(() => {
    if (data?.status === "draft" && !data.text) {
      setEditMode(true);
    }
  }, [data]);

  // ---- NEW: load full fact by id whenever drawer opens / id changes ----
  useEffect(() => {
    const id = articleId || data?.id;

    if (!id) {
      setFetchedFact(null);
      return;
    }

    let cancelled = false;

    (async () => {
      // setIsLoadingFact(true);
      try {
        const resp = await ApiFact.getById(id);
        const full = resp?.data?.fact || resp?.data || null;

        if (cancelled) return;
        setFetchedFact(full);
      } catch (e) {
        console.error("❌ Failed to load fact by id:", e);
      } finally {
        // if (!cancelled) setIsLoadingFact(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [articleId, data?.id]);

  const buildPendingSnapshot = useCallback(() => {
    const vals =
      typeof form?.getFieldsValue === "function" ? form.getFieldsValue() : {};
    return {
      ...viewData,
      ...vals,
      text: vals.text ?? viewData?.text ?? "",
      categoryId: vals.categoryId ?? viewData?.categoryId ?? null,
      tags: vals.tags ?? viewData?.tags ?? [],
      userId: vals.userId ?? viewData?.userId ?? null,
      FactUrl: vals.FactUrl ?? viewData?.FactUrl ?? null,
      FactCitation: vals.FactCitation ?? viewData?.FactCitation ?? null,
      color: vals.color ?? viewData?.color ?? "",
      title: vals.title ?? viewData?.title ?? "",
      associations: viewData?.associations,
    };
  }, [form, viewData]);

  const openChangeLogsModal = async () => {
    const pendingSnap = buildPendingSnapshot();
    setPendingVersion(pendingSnap);

    setDiffLoading(true);
    try {
      if (viewData?.id) {
        const resp = await ApiFact.getById(viewData.id, { status: "active" });
        const active = resp?.data?.fact || resp?.data || null;
        setActiveVersion(active);
      } else {
        setActiveVersion(null);
      }
    } catch {
      setActiveVersion(null);
    } finally {
      setDiffLoading(false);
      setOpenChangeLogsModal(true);
    }
  };

  const handleChangeLogsCancel = () => {
    setOpenChangeLogsModal(false);
  };

  // ---- NEW: normalize associations payload to groups for AssociationsBlock ----
  const associationGroups = useMemo(() => {
    const src = viewData?.associations;

    if (!src) return [];

    // associations: [{ key, title, count?, items: [{ id, label, href?, icon? }] }]
    if (Array.isArray(src)) {
      return src
        .filter(Boolean)
        .map((g) => ({
          key: g.key || g.title,
          title: g.title,
          count: Number.isInteger(g.count) ? g.count : undefined,
          items: Array.isArray(g.items)
            ? g.items.filter(Boolean).map((it) => ({
                id: it.id,
                label: it.label,
                href: it.href,
                onClick: it.onClick,
                icon: it.icon,
              }))
            : [],
        }))
        .filter((g) => g.title);
    }

    // associations: { tasks: {...}, scenarios: {...} }
    if (typeof src === "object") {
      return Object.entries(src)
        .map(([key, g]) => ({
          key,
          title: g?.title || key,
          count: Number.isInteger(g?.count) ? g.count : undefined,
          items: Array.isArray(g?.items)
            ? g.items.map((it) => ({
                id: it.id,
                label: it.label,
                href: it.href,
                icon: it.icon,
              }))
            : [],
        }))
        .filter((g) => g.title);
    }

    return [];
  }, [viewData]);

  const drawerContent = useMemo(() => {
    const d = viewData;

    return d ? (
      <Form form={form}>
        <DrawerHeader
          title={<p className="life-fact-title">{t("lifefacts.title")}</p>}
          onEdit={editHandler}
          onDiscard={openDiscardModal}
          onPublish={handlePublish}
          onCancel={cancelHandler}
          onClose={closeHandler}
          onViewChanges={openChangeLogsModal}
          isEditing={editMode}
          isLoading={isLoading}
          isChanged={isChanged}
          onToggleActive={() => toggleActive?.(d)}
          isActive={!!d?.isActive}
          onDelete={() => remove?.(d?.id, d?.title || d?.name || d?.text)}
          resourceTitle={d?.title || d?.text}
        />

        <div>
          <Form.Item name={"text"} initialValue={d.text}>
            <FormHeaderTextarea
              data-testid="fact-text"
              data-ready={isLoading ? "false" : "true"}
              readonly={!editMode}
              maxLength={2000}
              placeholder={t("lifefacts.placeholder.enter_fitfact")}
              onChange={(e) => handleFieldChange("text", e.target.value)}
            />
          </Form.Item>
        </div>

        <Form.Item>
          <FormFieldName text="Status:" />
          <div style={{ marginLeft: 12 }}>
            <UserActivityStatus userStatus={d.statusSummary} />
          </div>
        </Form.Item>

        <Form.Item name={"FactUrl"} initialValue={d.FactUrl}>
          <FormFieldName text={t("lifefacts.fields_name.source_url")} />
          <div style={{ marginTop: -4, width: "100%" }}>
            <FormSelectWithSearch
              canAddNew
              isURL
              multiline
              onSelect={updateSource}
              options={factUrls.map((f) => ({
                id: f.id,
                value: f.source?.trim(),
                subLabel: f.title?.trim(),
              }))}
              defaultValue={
                d.FactUrl && {
                  id: d?.FactUrl?.id,
                  value: d?.FactUrl?.source?.trim(),
                  subLabel: d?.FactUrl?.title?.trim(),
                }
              }
              readonly={!editMode}
              placeholder={editMode && t("lifefacts.placeholder.enter_source_url")}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
        </Form.Item>

        <Form.Item name={"FactCitation"} initialValue={d.FactCitation}>
          <FormFieldName text={t("lifefacts.fields_name.source_citation")} />
          <div style={{ marginTop: -4, width: "100%" }}>
            <FormSelectWithSearch
              canAddNew
              multiline
              addBtnText={t("lifefacts.btnText.add_citation")}
              onSelect={updateCitation}
              placeholder={editMode && t("lifefacts.placeholder.enter_citation")}
              options={factCitations.map((f) => ({
                id: f.id,
                value: f.source,
              }))}
              defaultValue={
                d.FactCitation && {
                  id: d.FactCitation.id,
                  value: d.FactCitation.source,
                }
              }
              readonly={!editMode}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
        </Form.Item>

        <Form.Item name={"categoryId"} initialValue={d.category}>
          <FormFieldName text="Category:" />
          <FormSelect
            options={categories.map((c) => ({
              value: c.id,
              label: c.name,
              key: c.id,
            }))}
            defaultValue={categories
              .map((cat) => ({ value: cat.id, key: cat.id, label: cat.name }))
              .find((c) => c.value === d.categoryId)}
            onSelect={updateCategory}
            placeholder={editMode && "Select category..."}
            style={{ minWidth: 160 }}
            readonly={!editMode}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </Form.Item>

        <Form.Item name={"tags"} initialValue={d.tags}>
          <FormFieldName text="Tags:" />
          <div style={{ marginLeft: 11 }}>
            <TagsSelect
              options={tags}
              defaultValue={d.tags}
              onChange={updateTags}
              onCreate={updateTags}
              readonly={!editMode}
              placeholder="+ Add Tag"
              noMarginLeft={true}
              isEditing={!editMode}
            />
          </div>
        </Form.Item>

        <Form.Item name={"color"} initialValue={d.color}>
          <FormFieldName text="Color:" />
          <div style={{ marginLeft: 4, marginTop: -12 }}>
            <SimpleColorPicker
              onChange={updateColor}
              defaultValue={d.color}
              readonly={!editMode}
              opened={editMode}
            />
          </div>
        </Form.Item>

        <Form.Item
          name={"userId"}
          initialValue={authors.find((a) => a.id === d.userId)?.id}
        >
          <FormFieldName text="Author:" />
          <FormSelect
            placeholder={editMode && "Select author"}
            options={authors?.map((author) => ({
              id: author.id,
              value: author.fullName,
            }))}
            defaultValue={() => {
              const usr = authors.find((a) => a.id === d.userId) || currentUser;
              return usr ? { id: usr?.id, value: usr?.fullName } : null;
            }}
            onSelect={updateAuthor}
            readonly={!editMode}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </Form.Item>

        {associationGroups?.length ? (
          <AssociationsBlock groups={associationGroups} />
        ) : null}
      </Form>
    ) : null;
  }, [
    viewData,
    associationGroups,
    editMode,
    isChanged,
    isLoading,
    // isLoadingFact
    form,
    authors,
    categories,
    tags,
    factUrls,
    factCitations,
    currentUser,
    t,

    editHandler,
    openDiscardModal,
    handlePublish,
    cancelHandler,
    closeHandler,
    openChangeLogsModal,
    toggleActive,
    remove,
  ]);

  return (
    <>
      <Drawer open={!!viewData} onClose={closeHandler} destroyOnHidden={true}>
        {drawerContent}
      </Drawer>

      <DiscardModal
        open={isDiscardModalOpen}
        onConfirm={async () => {
          await handleDiscard();
          closeDiscardModal();
          onClose();
        }}
        onCancel={closeDiscardModal}
      />

      <ChangesDiffModalComponent
        key={`diff-${viewData?.id || "new"}`}
        isModalOpen={isOpenChangeLogsModal}
        handleCancel={handleChangeLogsCancel}
        title={"Changes"}
        loading={diffLoading}
        pending={pendingVersion}
        active={activeVersion}
        fields={diffFields}
        titleFieldKey="title"
      />
    </>
  );
};

export default LifeFactsModal;
